import moment from 'moment';

import templateUrl from './scheduler-day-pivot-calendar.component.html';
import './scheduler-day-pivot-calendar.component.less';
import { segregateTimeSlots } from './scheduler-util'

/**
 * A calendar format that shows a date range in columns on a scheduler
 * calendar.
 */
export const SchedulerDayPivotCalendarComponent = {
    templateUrl,
    bindings: {
        schedules: "<",
        calendarDates: "<", // The calendar date range this is based on.
        caption: "@", // Caption for the scheduler, displayed in top left corner.
        scheduleActions: "<?", // Defines a list of additional actions. See ScheduleAction interface.
        editSchedule: "&onEditSchedule",
        deleteSchedule: "&onDeleteSchedule",
        dropIntoSchedule: "&onDropIntoSchedule",
        clickTimeSlot: "&onClickTimeSlot", // Pass schedule and optional time slot data.
        doubleClickTimeSlot: "&onDoubleClickTimeSlot", // Pass schedule and optional time slot data.
        createTimeSlot: "&onCreateTimeSlot", // Signal that the scheduler created a time slot on a schedule.
        deleteTimeSlot: "&onDeleteTimeSlot", // Signal that the scheduler is deleting a time slot.
        editTimeSlot: "&onEditTimeSlot", // Signal that the scheduler is editing a time slot.
        moveTimeSlot: "&onMoveTimeSlot" // Propagate event when resizing or moving a time slot between schedules.
    },
    controller: class SchedulerDayPivotCalendarComponent {
        $onInit() {
            this.caption = this.caption || "Schedules";
            this.scheduleActions = this.scheduleActions || [];
        }

        $onChanges(changesObj) {
            if (changesObj.schedules && this.schedules && this.calendarDates) {
                this.schedules = this.schedules.map(schedule => ({
                    ...schedule,
                    dateLanes: segregateTimeSlots(this.calendarDates, schedule.timeSlots, 'day')
                }));
            }
        }

        onClickTimeSlot({ schedule, date, timeSlot }, ev) {
            // Click events are attached to both the time slot and date container element.
            ev.stopPropagation();

            if (timeSlot && !timeSlot.readOnly && (timeSlot.editable || timeSlot.deletable)) {
                const newValue = !timeSlot.showOptions;
                this.schedules.forEach(schedule => schedule.timeSlots
                    .forEach(timeSlot => timeSlot.showOptions = false));
                // Toggle the timeslot options popover.
                timeSlot.showOptions = newValue;
            }

            this.clickTimeSlot({
                $event: {
                    schedule,
                    timeSlot: timeSlot || {
                        startDateTime: moment(date).startOf("day").toDate(),
                        endDateTime: moment(date).endOf("day").toDate()
                    }
                }
            });
        }

        onDoubleClickTimeSlot({ schedule, date, timeSlot }, ev) {
            this.doubleClickTimeSlot({
                $event: {
                    schedule,
                    timeSlot: timeSlot || {
                        startDateTime: moment(date).startOf("day").toDate(),
                        endDateTime: moment(date).endOf("day").toDate()
                    }
                }
            });
        }

        onEditSchedule(schedule) {
            return this.editSchedule({
                $event: {
                    schedule: schedule
                }
            });
        }

        onDeleteSchedule(schedule) {
            return this.deleteSchedule({
                $event: {
                    schedule: schedule
                }
            });
        }

        onDropIntoSchedule(schedule, payload) {
            return this.dropIntoSchedule({
                $event: {
                    schedule,
                    payload
                }
            })
        }

        onEditTimeSlot(schedule, timeSlot) {
            return this.editTimeSlot({
                $event: {
                    schedule,
                    timeSlot
                }
            })
        }

        onDeleteTimeSlot(schedule, timeSlot) {
            return this.deleteTimeSlot({
                $event: {
                    schedule,
                    timeSlot
                }
            })
        }

        onMoveTimeSlot({ schedule, timeSlot, newSchedule, newDate }) {
            return this.moveTimeSlot({
                $event: {
                    oldValue: {
                        schedule,
                        timeSlot
                    },
                    newValue: {
                        schedule: newSchedule,
                        timeSlot: {
                            startDateTime: moment(newDate).startOf("day").toDate(),
                            endDateTime: moment(newDate).endOf("day").toDate()
                        }
                    }
                }
            });
        }

        onDropSchedulePayload({ payload: { schedule, timeSlot }, newSchedule, newDate, dropEffect }) {
            // Handle drop operations. If payload has an id, perform a move.
            // Otherwise, create a new time slot from the payload data.
            if (timeSlot.id && dropEffect !== 'copy') {
                return this.onMoveTimeSlot({ schedule, timeSlot, newSchedule, newDate });
            } else {
                return this.createTimeSlot({
                    $event: {
                        schedule: newSchedule,
                        date: newDate,
                        timeSlot: {
                            ...timeSlot,
                            startDateTime: moment(newDate).startOf("day").toDate(),
                            endDateTime: moment(newDate).endOf("day").toDate()
                        }

                    }
                });
            }
        }
    }
}
